import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { ProductDetail } from '@/features/pdp/domain/models/ProductDetail';
import { buildPdpSeoFromProductDetail, getPdpRobotsMeta } from '@/features/pdp/utils/pdpSeo';

/**
 * Sets document head from PDP product API SEO (title, description, canonical, keywords, robots, schema, og tags).
 * Runs on client; SSR injects the same via injectPdpSeo when pdpData is present.
 */
export function PdpSeoHead({ productDetail }: { productDetail: ProductDetail | null | undefined }) {
  const location = useLocation();
  const titleSetForSeoRef = useRef<string | null>(null);

  useEffect(() => {
    if (!productDetail) return;

    const seo = buildPdpSeoFromProductDetail(productDetail, location.pathname);
    const robotsContent = getPdpRobotsMeta();
    const shareUrl = seo.shareUrl || seo.canonical;
    const seoKey = `${location.pathname}::${seo.canonical}::${seo.title}`;

    if (titleSetForSeoRef.current !== seoKey) {
      document.title = seo.title;
      titleSetForSeoRef.current = seoKey;
    }

    if (seo.description) {
      setMeta('description', seo.description);
      setMeta('og:description', seo.description, 'property');
      setMeta('twitter:description', seo.description);
    }
    if (seo.keywords) {
      setMeta('keywords', seo.keywords);
    }

    setMeta('robots', robotsContent);
    setCanonical(seo.canonical);

    if (shareUrl) {
      setMeta('og:url', shareUrl, 'property');
      setMeta('twitter:url', shareUrl);
    }

    setMeta('og:title', seo.ogTitle, 'property');
    setMeta('twitter:title', seo.ogTitle);
    if (seo.ogImage) {
      setMeta('og:image', seo.ogImage, 'property');
      setMeta('twitter:image', seo.ogImage);
    }
    setMeta('og:site_name', 'BlueStone', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@bluestone_com');

    setPdpSchemas(seo.schemas);
  }, [
    location.pathname,
    productDetail,
    productDetail?.designId,
    productDetail?.designName,
    productDetail?.metaDescription,
    productDetail?.metaKeywords,
    productDetail?.canonical,
    productDetail?.shareUrl,
    productDetail?.schema,
    productDetail?.breadCrumbListSchema,
    productDetail?.imageSchema,
    productDetail?.videoSchema,
    productDetail?.mediaItems,
  ]);

  return null;
}

function setMeta(nameOrProperty: string, content: string, attr: 'name' | 'property' = 'name') {
  const selector = attr === 'property' ? `meta[property="${nameOrProperty}"]` : `meta[name="${nameOrProperty}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, nameOrProperty);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function setPdpSchemas(schemas: Array<{ key: string; content: string }>) {
  const activeKeys = new Set(schemas.map((s) => s.key));

  document.querySelectorAll('script[data-pdp-schema]').forEach((node) => {
    const key = node.getAttribute('data-pdp-schema');
    if (key && !activeKeys.has(key)) node.remove();
  });

  for (const { key, content } of schemas) {
    let script = document.querySelector(`script[data-pdp-schema="${key}"]`);
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-pdp-schema', key);
      document.head.appendChild(script);
    }
    script.textContent = content.replace(/__BASE_URL__/g, window.location.origin);
  }
}
